// Risk Assignment Modal
// Dedicated modal for assigning risks to users and updating risk properties

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { riskPoolService } from '@/services/riskPoolService';
import { useLanguage } from '@/state/language/LanguageProvider';
// Removed unused imports
import api from '@/services/api';
import { toast } from 'react-hot-toast';

interface RiskAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  risk: any;
  onAssignmentComplete?: () => void;
}

export const RiskAssignmentModal: React.FC<RiskAssignmentModalProps> = ({
  isOpen,
  onClose,
  risk,
  onAssignmentComplete
}) => {
  const { t } = useLanguage();
  // Removed unused variables
  const [loading, setLoading] = useState(false);

  // Risk level options for escalation - moved inside component to access t function
  const escalationLevelOptions = [
    { value: '', label: t('risk.quickCreate.optional') },
    { value: 'VISION', label: t('risk.levels.vision') },
    { value: 'STRATEGY', label: t('risk.levels.strategy') },
    { value: 'PROGRAM', label: t('risk.levels.program') },
    { value: 'PROJECT', label: t('risk.levels.project') },
    { value: 'ORGANIZATION', label: t('risk.levels.organization') },
    { value: 'FUNCTION', label: t('risk.levels.function') },
    { value: 'GEOGRAPHY', label: t('risk.levels.geography') },
    { value: 'PROCESS', label: t('risk.levels.process') },
    { value: 'SYSTEM', label: t('risk.levels.system') }
  ];
  
  // Assignment form state
  const [assignmentData, setAssignmentData] = useState({
    current_owner_id: '',
    escalation_level: '',
    business_hierarchy_id: '',
    functional_hierarchy_id: '',
    geographic_hierarchy_id: '',
    assignment_reason: '',
    due_date: ''
  });

  // Options data
  const [users, setUsers] = useState<any[]>([]);
  const [businessHierarchies, setBusinessHierarchies] = useState<any[]>([]);
  const [functionalHierarchies, setFunctionalHierarchies] = useState<any[]>([]);
  const [geographicHierarchies, setGeographicHierarchies] = useState<any[]>([]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && risk) {
      loadInitialData();
    }
  }, [isOpen, risk]);

  // Debug: Log assignment data changes
  useEffect(() => {
      }, [assignmentData]);

  const loadInitialData = async () => {
    try {
      // Debug: Log the risk object to see what fields are available
            
      // Set initial values from risk
      const initialData = {
        current_owner_id: risk.current_owner_id || '',
        escalation_level: risk.escalation_level || '',
        business_hierarchy_id: risk.business_hierarchy_id || '',
        functional_hierarchy_id: risk.functional_hierarchy_id || '',
        geographic_hierarchy_id: risk.geographic_hierarchy_id || '',
        assignment_reason: '',
        due_date: risk.due_date ? risk.due_date.split('T')[0] : ''
      };
      
            setAssignmentData(initialData);

      // Load options in parallel
      await Promise.all([
        loadUsers(),
        loadHierarchies()
      ]);

      // Debug: Log hierarchy options
                        
      // Debug: Check if the current hierarchy IDs exist in the options
      const businessHierarchyExists = businessHierarchies.find(h => h.id === risk.business_hierarchy_id);
      const geographicHierarchyExists = geographicHierarchies.find(h => h.id === risk.geographic_hierarchy_id);
      
                  
                  
      if (businessHierarchyExists) {
        // hierarchy exists, no action needed
      } else {
        console.log('Business hierarchy ID not found in options. Available IDs:', businessHierarchies.map(h => h.id));
        console.log('First few business hierarchy options:', businessHierarchies.slice(0, 3));
      }
      
      if (geographicHierarchyExists) {
        // hierarchy exists, no action needed
      } else {
        console.log('Geographic hierarchy ID not found in options. Available IDs:', geographicHierarchies.map(h => h.id));
        console.log('First few geographic hierarchy options:', geographicHierarchies.slice(0, 3));
      }

      // Update assignment data with hierarchy IDs after options are loaded
      setAssignmentData(prev => ({
        ...prev,
        business_hierarchy_id: risk.business_hierarchy_id || prev.business_hierarchy_id,
        functional_hierarchy_id: risk.functional_hierarchy_id || prev.functional_hierarchy_id,
        geographic_hierarchy_id: risk.geographic_hierarchy_id || prev.geographic_hierarchy_id
      }));

      // If hierarchy IDs are not available but names are, try to find them by name
      if (!risk.business_hierarchy_id && risk.business_hierarchy_name) {
        const businessHierarchy = businessHierarchies.find(h => h.name === risk.business_hierarchy_name);
        if (businessHierarchy) {
                    setAssignmentData(prev => ({ ...prev, business_hierarchy_id: businessHierarchy.id }));
        }
      }

      if (!risk.functional_hierarchy_id && risk.functional_hierarchy_name) {
        const functionalHierarchy = functionalHierarchies.find(h => h.name === risk.functional_hierarchy_name);
        if (functionalHierarchy) {
                    setAssignmentData(prev => ({ ...prev, functional_hierarchy_id: functionalHierarchy.id }));
        }
      }

      if (!risk.geographic_hierarchy_id && risk.geographic_hierarchy_name) {
        const geographicHierarchy = geographicHierarchies.find(h => h.name === risk.geographic_hierarchy_name);
        if (geographicHierarchy) {
                    setAssignmentData(prev => ({ ...prev, geographic_hierarchy_id: geographicHierarchy.id }));
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/generic/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadHierarchies = async () => {
    try {
            
      // Load each hierarchy separately to catch individual errors
      let businessRes, functionalRes, geographicRes;
      
      try {
        businessRes = await api.get('/hierarchy/business');
              } catch (error) {
        console.error('Business hierarchy API call failed:', error);
        businessRes = { data: { success: false, data: [] } };
      }
      
      try {
        functionalRes = await api.get('/hierarchy/functional');
              } catch (error) {
        console.error('Functional hierarchy API call failed:', error);
        functionalRes = { data: { success: false, data: [] } };
      }
      
      try {
        geographicRes = await api.get('/hierarchy/geographic');
              } catch (error) {
        console.error('Geographic hierarchy API call failed:', error);
        geographicRes = { data: { success: false, data: [] } };
      }

                                          
      // Check if the responses are successful
                        
      // Check the length of data arrays
                  
      setBusinessHierarchies(businessRes.data.data || []);
      setFunctionalHierarchies(functionalRes.data.data || []);
      setGeographicHierarchies(geographicRes.data.data || []);
      
                              
      // Debug: Check if the state was set correctly
      setTimeout(() => {
                              }, 100);
    } catch (error) {
      console.error('Error loading hierarchies:', error);
    }
  };

  // Helper function to format hierarchy name with simple dashes
  const formatHierarchyName = (name: string, level: number, type: string) => {
    if (!name) return 'Not assigned';
    
    // Simple dashes for each level
    const dashes = '--'.repeat(Math.max(0, level - 1));
    return `${dashes}${name} (${type})`;
  };

  // Helper function to sort hierarchies by parent-child relationships
  const sortHierarchiesByParentChild = (hierarchies: any[]) => {
    // First, sort by level and then by name within each level
    const sorted = hierarchies.sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      return a.name.localeCompare(b.name);
    });

    // Now reorganize to show parent-child relationships
    const result: any[] = [];
    const processed = new Set<string>();

    // Process each hierarchy item
    for (const item of sorted) {
      if (processed.has(item.id)) continue;

      // Add the current item
      result.push(item);
      processed.add(item.id);

      // Find and add all children of this item
      const addChildren = (parentId: string, level: number) => {
        const children = sorted.filter(h => 
          h.parent_id === parentId && h.level === level + 1 && !processed.has(h.id)
        );

        for (const child of children) {
          result.push(child);
          processed.add(child.id);
          // Recursively add grandchildren
          addChildren(child.id, child.level);
        }
      };

      addChildren(item.id, item.level);
    }

    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!assignmentData.assignment_reason.trim()) {
      toast.error('Assignment reason is required');
      return;
    }

    setLoading(true);
    try {
      // Update risk with assignment data
      const updateData = {
        current_owner_id: assignmentData.current_owner_id || null,
        escalation_level: assignmentData.escalation_level || null,
        business_hierarchy_id: assignmentData.business_hierarchy_id || null,
        functional_hierarchy_id: assignmentData.functional_hierarchy_id || null,
        geographic_hierarchy_id: assignmentData.geographic_hierarchy_id || null,
        due_date: assignmentData.due_date || null
      };

            
      await riskPoolService.update(risk.id, updateData);

      // Add assignment comment
      if (assignmentData.assignment_reason) {
        await api.post(`/risks/${risk.id}/comments`, {
          comment_text: `Assignment: ${assignmentData.assignment_reason}`,
          comment_type: 'ASSIGNMENT'
        });
      }

      toast.success('Risk assigned successfully');
      onAssignmentComplete?.();
      onClose();
    } catch (error) {
      console.error('Error assigning risk:', error);
      console.error('Error details:', (error as any)?.response?.data || (error as any)?.message);
      toast.error('Failed to assign risk');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAssignmentData({
      current_owner_id: '',
      escalation_level: '',
      business_hierarchy_id: '',
      functional_hierarchy_id: '',
      geographic_hierarchy_id: '',
      assignment_reason: '',
      due_date: ''
    });
    onClose();
  };

  // Removed duplicate function declarations

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Assign Risk: ${risk?.name || ''}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Risk Owner Assignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assign to Owner
          </label>
          <Select
            value={assignmentData.current_owner_id}
            onChange={(e) => setAssignmentData(prev => ({ ...prev, current_owner_id: e.target.value }))}
            options={[
              { value: '', label: 'Unassigned' },
              ...users.map(user => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name}`
              }))
            ]}
          />
        </div>

        {/* Risk Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Risk Level
          </label>
          <Select
            value={assignmentData.escalation_level}
            onChange={(e) => setAssignmentData(prev => ({ ...prev, escalation_level: e.target.value }))}
            options={escalationLevelOptions}
          />
        </div>

        {/* Business Hierarchy */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Business Hierarchy
          </label>
          <Select
            value={assignmentData.business_hierarchy_id}
            onChange={(e) => {
                            setAssignmentData(prev => ({ ...prev, business_hierarchy_id: e.target.value }));
            }}
            options={(() => {
              const options = [
                { value: '', label: 'Not assigned' },
                ...sortHierarchiesByParentChild(businessHierarchies)
                  .map(h => ({
                    value: h.id,
                    label: formatHierarchyName(h.name, h.level, h.level_type)
                  }))
              ];
                            return options;
            })()}
          />
          {/* Debug: Show current value */}
          <div className="text-xs text-gray-500 mt-1">
            Debug: Current business_hierarchy_id = &quot;{assignmentData.business_hierarchy_id}&quot;
          </div>
        </div>

        {/* Functional Hierarchy */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Functional Hierarchy
          </label>
          <Select
            value={assignmentData.functional_hierarchy_id}
            onChange={(e) => setAssignmentData(prev => ({ ...prev, functional_hierarchy_id: e.target.value }))}
            options={[
              { value: '', label: 'Not assigned' },
              ...sortHierarchiesByParentChild(functionalHierarchies)
                .map(h => ({
                  value: h.id,
                  label: formatHierarchyName(h.name, h.level, h.level_type)
                }))
            ]}
          />
        </div>

        {/* Geographic Hierarchy */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Geographic Hierarchy
          </label>
          <Select
            value={assignmentData.geographic_hierarchy_id}
            onChange={(e) => {
                            setAssignmentData(prev => ({ ...prev, geographic_hierarchy_id: e.target.value }));
            }}
            options={(() => {
              const options = [
                { value: '', label: 'Not assigned' },
                ...sortHierarchiesByParentChild(geographicHierarchies)
                  .map(h => ({
                    value: h.id,
                    label: formatHierarchyName(h.name, h.level, h.level_type)
                  }))
              ];
                            return options;
            })()}
          />
          {/* Debug: Show current value */}
          <div className="text-xs text-gray-500 mt-1">
            Debug: Current geographic_hierarchy_id = &quot;{assignmentData.geographic_hierarchy_id}&quot;
          </div>
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Due Date
          </label>
          <Input
            type="date"
            value={assignmentData.due_date}
            onChange={(e) => setAssignmentData(prev => ({ ...prev, due_date: e.target.value }))}
          />
        </div>

        {/* Assignment Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Reason <span className="text-red-500">*</span>
          </label>
          <Textarea
            value={assignmentData.assignment_reason}
            onChange={(e) => setAssignmentData(prev => ({ ...prev, assignment_reason: e.target.value }))}
            placeholder="Explain why this risk is being assigned and any specific instructions..."
            rows={3}
            required
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Assigning...' : 'Assign Risk'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
